import { useEffect, useRef, useState } from 'react';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import { api } from './api';

/**
 * §19 M9.3 live chat over the Yjs gateway. A room's `meta` map only ever carries a revision stamp written by the
 * server ("something changed — refetch"); content always comes from the API, so RLS decides what each person sees.
 * Typing indicators ride on the room's awareness. The provider is imported lazily: it isn't in the app shell.
 */
export function useChatRoom(
  room: string | null,
  tokenQuery: string,
  metaKey: 'rev' | 'chatRev',
  onChange: () => void,
  me?: { id: string; name: string },
) {
  const cb = useRef(onChange);
  cb.current = onChange;
  const provider = useRef<HocuspocusProvider | null>(null);
  const [typing, setTyping] = useState<string[]>([]);
  useEffect(() => {
    if (!room) return;
    let p: HocuspocusProvider | null = null;
    let cancelled = false;
    void import('@hocuspocus/provider').then(({ HocuspocusProvider: Provider }) => {
      if (cancelled) return;
      p = new Provider({
        url: `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/rt`,
        name: room,
        token: async () => (await api.get<{ token: string }>(`/chat/realtime-token${tokenQuery}`)).token,
      });
      provider.current = p;
      const meta = p.document.getMap<number>('meta');
      meta.observe((e) => e.keysChanged.has(metaKey) && cb.current());
      if (me) {
        p.setAwarenessField('user', { id: me.id, name: me.name, typing: false });
        p.awareness!.on('change', () => {
          const names = [...p!.awareness!.getStates().entries()]
            .filter(([id]) => id !== p!.awareness!.clientID)
            .map(([, s]) => s.user as { id: string; name: string; typing: boolean } | undefined)
            .filter((u): u is { id: string; name: string; typing: boolean } => !!u?.typing && u.id !== me.id)
            .map((u) => u.name);
          setTyping([...new Set(names)]);
        });
      }
    });
    return () => {
      cancelled = true;
      p?.destroy();
      provider.current = null;
      setTyping([]);
    };
    // One connection per room.
  }, [room, tokenQuery, metaKey, me?.id]);

  /** Tell the room I'm typing (cleared by the caller after a pause or on send). */
  const announce = (on: boolean) => {
    const p = provider.current;
    if (p && me) p.setAwarenessField('user', { id: me.id, name: me.name, typing: on });
  };
  return { typing, announce };
}
